/**
 * AsyncState: one way to show loading, error and empty for anything fetched.
 *
 *   <AsyncState
 *     loading={loading}
 *     error={error}                       string | Error | true; shown with a Retry button when onRetry is set
 *     onRetry={reload}
 *     empty={!loading && keys.length === 0}
 *     emptyState={{ title: "No API keys yet", description: "Create one to call the API.", action: <Button>Create key</Button> }}
 *     skeleton={<SkeletonRows rows={4} />}   optional; defaults to three text lines
 *   >
 *     <KeysTable keys={keys} />
 *   </AsyncState>
 *
 * Order: first load shows the skeleton (never the empty state); an error with nothing loaded yet shows ErrorState;
 * empty shows EmptyBlock; otherwise children. With `hasData` set, a later error or reload keeps the children and
 * shows a slim banner or progress line on top instead of throwing the data away.
 *
 * Also exported for direct use: Skeleton, SkeletonText, SkeletonRows, ErrorState, EmptyBlock.
 */
import { ReactNode } from "react";
import { Icon } from "@iconify/react";
import Button from "./Button";

// ---------- skeletons ----------
export function Skeleton({ className = "" }: { className?: string }) {
  return <div aria-hidden="true" className={`animate-pulse rounded-md bg-gray-200/80 dark:bg-white/10 ${className}`} />;
}

export function SkeletonText({ lines = 3, className = "" }: { lines?: number; className?: string }) {
  return (
    <div className={`space-y-2.5 ${className}`} aria-hidden="true">
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} className={`h-3.5 ${i === lines - 1 ? "w-3/5" : "w-full"}`} />
      ))}
    </div>
  );
}

/** Table-shaped skeleton: a header line and `rows` rows of `cols` cells. */
export function SkeletonRows({ rows = 5, cols = 4, className = "" }: { rows?: number; cols?: number; className?: string }) {
  return (
    <div className={`divide-y divide-line ${className}`} aria-hidden="true">
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} className="flex items-center gap-6 px-4 py-3.5">
          {Array.from({ length: cols }, (_, c) => (
            <Skeleton key={c} className={`h-3.5 ${c === 0 ? "flex-[2]" : "flex-1"}`} />
          ))}
        </div>
      ))}
    </div>
  );
}

// ---------- error ----------
export function errorMessage(error: unknown): string {
  if (!error || error === true) return "Something went wrong while loading this.";
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;
  return "Something went wrong while loading this.";
}

export function ErrorState({
  title = "Couldn't load this",
  error,
  onRetry,
  retrying = false,
  compact = false,
  className = "",
}: {
  title?: string;
  error?: unknown;
  onRetry?: () => void;
  retrying?: boolean;
  /** One-line banner instead of a centred block; used above data that is still shown. */
  compact?: boolean;
  className?: string;
}) {
  if (compact) {
    return (
      <div
        role="alert"
        className={`flex flex-wrap items-center gap-x-3 gap-y-2 rounded-control border border-danger/30 bg-danger-subtle px-3 py-2 text-13 text-danger-text ${className}`}
      >
        <Icon icon="mdi:alert-circle-outline" className="shrink-0 text-base" aria-hidden="true" />
        <span className="min-w-0 flex-1">{errorMessage(error)}</span>
        {onRetry && (
          <Button size="sm" variant="secondary" onClick={onRetry} loading={retrying}>
            Retry
          </Button>
        )}
      </div>
    );
  }
  return (
    <div role="alert" className={`flex flex-col items-center justify-center px-6 py-12 text-center ${className}`}>
      <span className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-danger-subtle text-danger-text">
        <Icon icon="mdi:alert-circle-outline" className="text-xl" aria-hidden="true" />
      </span>
      <p className="text-sm font-semibold text-fg">{title}</p>
      <p className="mt-1 max-w-sm text-13 text-fg-muted">{errorMessage(error)}</p>
      {onRetry && (
        <Button className="mt-4" size="sm" variant="secondary" leftIcon="mdi:refresh" onClick={onRetry} loading={retrying}>
          Retry
        </Button>
      )}
    </div>
  );
}

// ---------- empty ----------
export interface EmptyStateConfig {
  title: string;
  description?: ReactNode;
  icon?: string;
  action?: ReactNode;
}

export function EmptyBlock({ title, description, icon = "mdi:tray", action, className = "" }: EmptyStateConfig & { className?: string }) {
  return (
    <div className={`flex flex-col items-center justify-center px-6 py-12 text-center ${className}`}>
      <span className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-fg-muted dark:bg-white/10">
        <Icon icon={icon} className="text-xl" aria-hidden="true" />
      </span>
      <p className="text-sm font-semibold text-fg">{title}</p>
      {description && <div className="mt-1 max-w-sm text-13 text-fg-muted">{description}</div>}
      {action && <div className="mt-4 flex flex-wrap justify-center gap-2">{action}</div>}
    </div>
  );
}

// ---------- wrapper ----------
interface AsyncStateProps {
  loading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  empty?: boolean;
  emptyState?: EmptyStateConfig;
  /** Custom empty node; wins over emptyState. */
  emptyContent?: ReactNode;
  skeleton?: ReactNode;
  errorTitle?: string;
  /** Data from an earlier load is on screen; keep it through reloads and errors. */
  hasData?: boolean;
  /** Accessible name for the loading region, e.g. "Loading API keys". */
  loadingLabel?: string;
  children?: ReactNode;
  className?: string;
}

export default function AsyncState({
  loading = false,
  error,
  onRetry,
  empty = false,
  emptyState,
  emptyContent,
  skeleton,
  errorTitle,
  hasData = false,
  loadingLabel = "Loading",
  children,
  className = "",
}: AsyncStateProps) {
  if (hasData) {
    return (
      <div className={`relative ${className}`} aria-busy={loading || undefined}>
        {loading && (
          <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-0.5 overflow-hidden" aria-hidden="true">
            <div className="h-full w-1/3 animate-[asyncstate-bar_1.1s_ease-in-out_infinite] bg-primary" />
          </div>
        )}
        {error ? <ErrorState compact error={error} onRetry={onRetry} className="mb-3" /> : null}
        {children}
      </div>
    );
  }
  if (loading) {
    return (
      <div className={className} role="status" aria-live="polite" aria-busy="true">
        <span className="sr-only">{loadingLabel}</span>
        {skeleton ?? <SkeletonText lines={3} className="p-5" />}
      </div>
    );
  }
  if (error) return <ErrorState className={className} title={errorTitle} error={error} onRetry={onRetry} />;
  if (empty) {
    if (emptyContent) return <>{emptyContent}</>;
    return <EmptyBlock className={className} {...(emptyState ?? { title: "Nothing here yet" })} />;
  }
  return <>{children}</>;
}
